import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import StarIcon from "@mui/icons-material/Star";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import {
  Box,
  ButtonBase,
  ListItem,
  ListItemButton,
  Tooltip,
} from "@mui/material";
import { usePrimaryBankingAccountMutation } from "Api/Mutations/Accounts/usePrimaryBankingAccountMutation";
import { useUserInfoQuery } from "Auth/Api/Queries/useUserInfoQuery";
import { isBankAccountDetailDisabled } from "Components/BankAccounts/BankAccountDetail/disabledAccountsUtils";
import { Account } from "Components/Shared/Account/Account";
import { useIsMobile } from "Hooks/useIsMobile";
import type { BankAccountListItem } from "Infrastructure/Api/Api";
import { Link } from "Navigation/Link";
import { Resources, useResource } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";
import { useState } from "react";

const DETAIL_RESOURCES = Resources.Pages.Content.BankAccounts.Detail;

const PRIMARY_ACTION_CLASS = "BankAccountRow-primaryAction";

type Props = {
  account: BankAccountListItem;
  isEdit?: boolean;
  hasPrimaryAction?: boolean;
};

export const BankAccountRow = ({
  account,
  isEdit,
  hasPrimaryAction,
}: Props) => {
  const { data: userInfo } = useUserInfoQuery();
  const isMobile = useIsMobile();
  const { t } = useResource();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const { mutate: savePrimaryAccount, isPending: isSetPrimaryPending } =
    usePrimaryBankingAccountMutation();

  const detailHref = `${appUrl("bank-account-detail")}/${account.publicID}`;
  const isSetPrimaryDisabled =
    isSetPrimaryPending || isBankAccountDetailDisabled(account);

  const accountContent = (
    <Account
      {...account}
      verificationStatusVisibility="all"
      // The star strip already says "primary of this currency", so the chip would
      // duplicate it — and because it only appears once the flag is set, the chip line reflowed
      // (and wrapped, the row is narrower by the star's reserved padding) on every click.
      // The edit-order variant has no star and keeps the chip.
      isPrimaryChipHidden={hasPrimaryAction}
      balanceVariant={
        account.isReceiveOnly
          ? "hidden"
          : userInfo?.isBalanceVisible === false
            ? "blurred"
            : "visible"
      }
    />
  );

  if (isEdit) {
    return (
      <ListItem disablePadding>
        <Box
          width="100%"
          overflow="hidden"
          px={4}
          py={2}
          sx={({ spacing, palette }) => ({
            borderRadius: spacing(2),
            border: `1px dashed ${palette.grey[300]}`,
          })}
        >
          {accountContent}
        </Box>
      </ListItem>
    );
  }

  // The star strip is a flex sibling of the row's Link, so setting the primary never
  // triggers navigation. Primary is per currency: the filled star of one currency group
  // is untouched by starring an account in another group. The filled star is inert, same
  // as the detail screen's action row, where the primary account offers no action either.
  // Both states share the one Tooltip > span > ButtonBase anchor on purpose: the click
  // flips isPrimary optimistically while the tooltip is open, and a remounted anchor
  // would flash the tooltip in the viewport corner until the popper re-anchors. Only
  // the icon and label swap. The disabled star shows no tooltip, and its visibility is
  // controlled through `open` rather than by emptying `title` — an emptied title would
  // fade out as a blank bubble.
  const primaryActionLabel = t(
    account.isPrimary
      ? DETAIL_RESOURCES.IsPrimaryNote
      : DETAIL_RESOURCES.Actions.SetAsPrimary,
  );
  const isPrimaryActionDisabled = !account.isPrimary && isSetPrimaryDisabled;
  const primaryAction = (
    <Tooltip
      title={primaryActionLabel}
      open={isTooltipOpen && !isPrimaryActionDisabled}
      onOpen={() => setIsTooltipOpen(true)}
      onClose={() => setIsTooltipOpen(false)}
    >
      <span className={PRIMARY_ACTION_CLASS} style={{ display: "flex" }}>
        <ButtonBase
          aria-label={primaryActionLabel}
          disabled={isPrimaryActionDisabled}
          onClick={
            account.isPrimary
              ? undefined
              : () => savePrimaryAccount(account.publicID)
          }
          sx={{
            pl: 2,
            pr: { xs: 2, sm: 4 },
            "&.Mui-disabled": { color: "action.disabled" },
            ...(account.isPrimary
              ? { cursor: "default" }
              : { "&:hover": { color: "warning.main" } }),
          }}
        >
          {account.isPrimary ? (
            <StarIcon color="warning" />
          ) : (
            <StarOutlineIcon />
          )}
        </ButtonBase>
      </span>
    </Tooltip>
  );

  return (
    <Box
      sx={({ spacing, palette }) => ({
        display: "flex",
        alignItems: "stretch",
        overflow: "hidden",
        borderRadius: spacing(2),
        border: `1px solid ${palette.grey[300]}`,
      })}
    >
      <ListItemButton
        component={Link}
        to={detailHref}
        sx={({ transitions }) => ({
          flex: 1,
          minWidth: 0,
          pl: 4,
          pr: hasPrimaryAction ? { xs: 0, sm: 1 } : 4,
          py: 2,
          color: "inherit",
          textDecoration: "none",
          alignItems: "center",
          gap: 1,
          // Hovering the account link highlights the star strip too, so the whole
          // row reads as hovered — but not vice versa: the star is a sibling, so
          // its own hover can't reach back to the link. The strip mirrors
          // ListItemButton's own background transition so both fade in sync.
          [`& + .${PRIMARY_ACTION_CLASS}`]: {
            transition: transitions.create("background-color", {
              duration: transitions.duration.shortest,
            }),
          },
          [`&:hover + .${PRIMARY_ACTION_CLASS}`]: { bgcolor: "action.hover" },
        })}
      >
        <Box flex={1} minWidth={0}>
          {accountContent}
        </Box>
        {!isMobile && !hasPrimaryAction && (
          <ChevronRightIcon sx={{ flexShrink: 0, color: "text.disabled" }} />
        )}
      </ListItemButton>
      {hasPrimaryAction && primaryAction}
    </Box>
  );
};
